// The account's data as the website uses it, converted from the server's rows.

import type { DistanceUnit, RateStart } from "./rates.ts";

export interface Vehicle {
  id: string;
  name: string;
  unit: DistanceUnit;
  createdAt: Date;
}

export interface Trip {
  id: string;
  vehicleId: string | null;
  start: Date;
  end: Date | null;
  meters: number;
  startName: string | null;
  endName: string | null;
  startCoordinate: [number, number] | null;
  endCoordinate: [number, number] | null;
  isAutomatic: boolean;
  isEnteredByHand: boolean;
  isRoundTrip: boolean;
  /** A built-in's name or a custom category's UUID string; null while unclassified. */
  categoryRaw: string | null;
  /** What chose the category when the user didn't; null once the user picks one. */
  classificationSource: string | null;
  purpose: string | null;
  notes: string | null;
  /** [latitude, longitude, seconds since start] */
  route: [number, number, number][];
  updatedAt: Date;
}

export interface SavedPlace {
  id: string;
  name: string;
  address: string | null;
  latitude: number;
  longitude: number;
  kind: string;
}

export interface Rate {
  id: string;
  year: number;
  start: RateStart;
  category: string;
  amount: number | null;
  unit: DistanceUnit;
  currencyCode: string;
  updatedAt: Date;
}

export interface AccountStatus {
  subscribedUntil: Date | null;
  canUpload: boolean;
  dataDeletedAfter: Date | null;
}

/** The trip as its title says it, as `TripRowView` does: "Home → Office", "Home ⇄ Office". */
export function tripTitle(trip: Pick<Trip, "startName" | "endName" | "isRoundTrip">): string {
  const { startName, endName } = trip;
  if (startName && endName) return trip.isRoundTrip ? `${startName} ⇄ ${endName}` : `${startName} → ${endName}`;
  return startName ?? endName ?? "Trip";
}

/** The title as a screen reader should say it: "Home to Office", not "Home right arrow Office". */
export function spokenTripTitle(trip: Pick<Trip, "startName" | "endName" | "isRoundTrip">): string {
  const { startName, endName } = trip;
  if (startName && endName) return trip.isRoundTrip ? `${startName} to ${endName} and back` : `${startName} to ${endName}`;
  return tripTitle(trip);
}

/** Seconds the trip took, or undefined while it has no end. */
export function tripDuration(trip: Pick<Trip, "start" | "end">): number | undefined {
  return trip.end ? Math.max(0, (trip.end.getTime() - trip.start.getTime()) / 1000) : undefined;
}

/** Meters per second over the whole trip. */
export function averageSpeed(trip: Pick<Trip, "start" | "end" | "meters">): number | undefined {
  const duration = tripDuration(trip);
  return duration && duration > 0 ? trip.meters / duration : undefined;
}
